import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Pagination,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Visibility as ViewIcon,
    CheckCircle as FinalizeIcon,
    Send as ShareIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import apiClient from '../../../api/client';

interface GRN {
    id: number;
    grn_number: string;
    po_number: string;
    batch_number: string;
    vendor_name: string;
    status: string;
    shared_with_farm: boolean;
    shared_with_accounts: boolean;
    created_at: string;
}

const GRNListPage: React.FC = () => {
    const navigate = useNavigate();
    const [grns, setGRNs] = useState<GRN[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const limit = 20;

    useEffect(() => {
        fetchGRNs();
    }, [page]);

    const fetchGRNs = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/grn-new/list', {
                params: { page, limit }
            });
            setGRNs(response.data.grns);
            setTotal(response.data.total);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to fetch GRNs');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        const colors: Record<string, 'default' | 'success'> = {
            draft: 'default',
            finalized: 'success'
        };
        return colors[status] || 'default';
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4">Goods Receipt Notes (GRN)</Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>GRN #</TableCell>
                                    <TableCell>PO #</TableCell>
                                    <TableCell>Batch #</TableCell>
                                    <TableCell>Vendor</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Farm</TableCell>
                                    <TableCell>Accounts</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {grns.map((grn) => (
                                    <TableRow key={grn.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {grn.grn_number}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{grn.po_number}</TableCell>
                                        <TableCell>
                                            <Chip label={grn.batch_number} size="small" color="primary" />
                                        </TableCell>
                                        <TableCell>{grn.vendor_name}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={grn.status.toUpperCase()}
                                                size="small"
                                                color={getStatusColor(grn.status)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {grn.shared_with_farm && <Chip label="✓" size="small" color="success" />}
                                        </TableCell>
                                        <TableCell>
                                            {grn.shared_with_accounts && <Chip label="✓" size="small" color="success" />}
                                        </TableCell>
                                        <TableCell>
                                            {format(new Date(grn.created_at), 'dd MMM yyyy')}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/inward/grn-new/${grn.id}`)}
                                                title="View Details"
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            count={Math.ceil(total / limit)}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default GRNListPage;
